import { useState, useEffect, useCallback } from 'react';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';

interface Description {
  content: string;
  timestamp: number;
}

export const useDescriptions = () => {
  const [descriptions, setDescriptions] = useState<Description[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeOrganisation] = useActiveOrganisation();

  const fetchDescriptions = useCallback(async () => {
    if (!activeOrganisation?.organizationId) return;

    try {
      const response = await fetch('/api/db/business/metadata/business_description', {
        headers: {
          'X-Organization': activeOrganisation.organizationId,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch descriptions');
      }

      const data = await response.json();
      if (data && data.value) {
        let descriptionsArray: Description[];
        try {
          const parsedValue = JSON.parse(data.value);
          if (Array.isArray(parsedValue)) {
            descriptionsArray = parsedValue.map(description => {
              if (typeof description === 'object' && description.content && description.timestamp) {
                return description as Description;
              }
              return {
                content: typeof description === 'string' ? description : String(description),
                timestamp: Date.now()
              };
            });
          } else {
            descriptionsArray = [{
              content: String(data.value),
              timestamp: Date.now()
            }];
          }
        } catch (e) {
          descriptionsArray = [{
            content: String(data.value),
            timestamp: Date.now()
          }];
        }
        setDescriptions(descriptionsArray);
      } else {
        setDescriptions([]);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [activeOrganisation?.organizationId]);

  // Initial fetch
  useEffect(() => {
    setIsLoading(true);
    fetchDescriptions();
  }, [fetchDescriptions]);

  // Refetch function
  const refetch = useCallback(async () => {
    setIsRefetching(true);
    await fetchDescriptions();
    setIsRefetching(false);
  }, [fetchDescriptions]);

  return {
    descriptions,
    error,
    refetch,
    isRefetching,
    isLoading,
  };
}; 